import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../../utils/api";

const TableSelectionScreen = () => {
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableOrders, setTableOrders] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [inactiveModal, setInactiveModal] = useState(null);
  
  // Dynamically determine the base route to avoid triggering 401 logouts
  const isStaffRoute = typeof window !== 'undefined' && window.location.pathname.includes('/restaurant-staff');
  const basePath = isStaffRoute ? '/restaurant-staff/employee' : '/restaurant';
  const apiPrefix = isStaffRoute ? 'restaurant-staff' : 'restaurant';

  useEffect(() => {
    const fetchTables = async () => {
      try {
        setLoading(true);
        const response = await api.get(`${apiPrefix}/order/tables`);
        setTables(response.data || []);
      } catch (error) {
        console.error("Error fetching tables:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTables();
  }, []);

  const handleTableSelect = (table) => {
    if (table.is_active === 1 && table.order_id === null) {
      // Start new order if active & no order assigned
      navigate(
        `${basePath}/create-order?type=table&tableNumber=${encodeURIComponent(
          table.id
        )}&tableName=${encodeURIComponent(table.name)}`
      );
    } else if (table.is_active === 1 && table.order_id !== null) {
      // Show modal if occupied
      setSelectedTable(table);
    } else {
      // Show modern modal for inactive table
      setInactiveModal(table);
    }
  };

  const closeModal = () => {
    setSelectedTable(null);
    setInactiveModal(null);
  };

  // Filtered tables based on status
  const filteredTables = tables.filter((table) => {
    const matchesSearch =
      table.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      table.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter =
      filter === "all" ||
      (filter === "available" && table.is_active === 1 && table.order_id === null) ||
      (filter === "occupied" && table.is_active === 1 && table.order_id !== null) ||
      (filter === "inactive" && table.is_active === 0);

    return matchesSearch && matchesFilter;
  });

  // Count Available
  const availableTables = tables.filter(
    (table) => table.is_active === 1 && table.order_id === null
  ).length;

  // Count Occupied
  const occupiedTables = tables.filter(
    (table) => table.is_active === 1 && table.order_id !== null
  ).length;

  // Count Inactive
  const inactiveTables = tables.filter(
    (table) => table.is_active === 0
  ).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-64 mb-6"></div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-gray-100 rounded-xl"></div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-40 bg-gray-100 rounded-2xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getTableStatus = (table) => {
    if (table.is_active === 0) return "inactive";
    if (table.order_id !== null) return "occupied";
    return "available";
  };

  const getStatusStyles = (status) => {
    const styles = {
      available: {
        bg: "bg-green-100",
        text: "text-green-800",
        dot: "bg-green-500",
        label: "Available"
      },
      occupied: {
        bg: "bg-red-100",
        text: "text-red-800",
        dot: "bg-red-500",
        label: "Occupied"
      },
      inactive: {
        bg: "bg-gray-100",
        text: "text-gray-600",
        dot: "bg-gray-400",
        label: "Inactive"
      }
    };
    return styles[status] || styles.inactive;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Table Management</h1>
              <p className="text-gray-600 mt-2">Efficiently manage your restaurant tables</p>
            </div>
            <Link to={`${basePath}/${isStaffRoute ? 'table' : 'tables'}`}
             
              className="mt-4 lg:mt-0 bg-[#c52140] hover:bg-[#a81b36] text-white px-4 py-1 rounded-lg font-semibold flex items-center space-x-2 transition-all duration-300 shadow-sm hover:shadow-md"
            >
              <span className="text-lg">+</span>
              <span>Create New Table</span>
            </Link>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-gray-600 text-sm">Total Tables</p>
                  <p className="text-2xl font-bold text-gray-900">{tables.length}</p>
                </div>
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                  <span className="text-gray-600 text-lg">🍽️</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-gray-600 text-sm">Available</p>
                  <p className="text-2xl font-bold text-green-600">{availableTables}</p>
                </div>
                <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                  <span className="text-green-600 text-lg">✓</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-gray-600 text-sm">Occupied</p>
                  <p className="text-2xl font-bold text-[#c52140]">{occupiedTables}</p>
                </div>
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                  <span className="text-[#c52140] text-lg">👥</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-gray-600 text-sm">Inactive</p>
                  <p className="text-2xl font-bold text-gray-500">{inactiveTables}</p>
                </div>
                <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center">
                  <span className="text-gray-500 text-lg">⏸️</span>
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="bg-white rounded-lg p-2 mb-6 border border-gray-200 shadow-sm">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search tables by name or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2 pl-10 text-gray-900 placeholder-gray-500 focus:outline-none focus:border-[#c52140] focus:ring-1 focus:ring-[#c52140]"
                  />
                  <div className="absolute left-3 top-3 text-gray-400">🔍</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                {[
                  { value: "all", label: "All Tables" },
                  { value: "available", label: "Available" },
                  { value: "occupied", label: "Occupied" },
                  { value: "inactive", label: "Inactive" }
                ].map((filterType) => (
                  <button
                    key={filterType.value}
                    onClick={() => setFilter(filterType.value)}
                    className={`px-4 h-8 rounded-lg font-medium transition-all duration-200 ${
                      filter === filterType.value
                        ? "bg-[#c52140] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {filterType.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Tables Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredTables.map((table) => {
            const status = getTableStatus(table);
            const statusStyles = getStatusStyles(status);
            const isActive = table.is_active === 1;

            return (
              <div
                key={table.id}
                onClick={() => handleTableSelect(table)}
                className={`group relative bg-white rounded-xl border-2 p-4 cursor-pointer transition-all duration-300 hover:shadow-lg ${
                  status === "inactive" 
                    ? "border-gray-300 hover:border-gray-400 opacity-80" 
                    : status === "occupied"
                    ? "border-red-200 hover:border-red-300"
                    : "border-green-200 hover:border-green-300"
                }`}
              >
                {/* Status Indicator */}
                <div className="flex justify-between items-start mb-3">
                  <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles.bg} ${statusStyles.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full mr-1 ${statusStyles.dot}`}></span>
                    {statusStyles.label}
                  </div>
                  <div className="text-gray-400 group-hover:text-gray-600">
                    ⌘
                  </div>
                </div>

                {/* Table Number */}
                <div className="text-center mb-4">
                  <div className={`w-14 h-14 mx-auto rounded-lg flex items-center justify-center transition-colors duration-300 ${
                    status === "inactive" 
                      ? "bg-gray-100 group-hover:bg-gray-200" 
                      : status === "occupied"
                      ? "bg-red-50 group-hover:bg-red-100"
                      : "bg-green-50 group-hover:bg-green-100"
                  }`}>
                    <span className={`text-xl font-bold ${
                      status === "inactive" 
                        ? "text-gray-500" 
                        : status === "occupied"
                        ? "text-[#c52140]"
                        : "text-green-700"
                    }`}>
                      {table.name}
                    </span>
                  </div>
                </div>

                {/* Table Info */}
                <div className="space-y-2">
                  <div>
                    <p className="text-gray-500 text-sm text-center mb-1">
                      {table.description || "Standard table"}
                    </p>
                  </div>
                  
                  <div className="flex justify-center space-x-4 text-xs text-gray-500">
                    <div className="flex items-center space-x-1">
                      <span>👥</span>
                      <span>{table.seating_capacity}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <span>🏢</span>
                      <span>{table.floor}</span>
                    </div>
                  </div>

                  {/* Action Button */}
                  <button className={`w-full mt-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
                    status === "inactive"
                      ? "bg-gray-400 text-white cursor-not-allowed"
                      : status === "occupied"
                      ? "bg-[#c52140] text-white hover:bg-[#a81b36]"
                      : "bg-gray-900 text-white hover:bg-gray-800"
                  }`}>
                    {status === "inactive" ? "Table Inactive" : 
                     status === "occupied" ? "View Orders" : "Start New Order"}
                  </button>
                  
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredTables.length === 0 && (
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <span className="text-3xl">🍽️</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No tables found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search or filter criteria</p>
            <button
              onClick={() => {
                setSearchTerm("");
                setFilter("all");
              }}
              className="bg-[#c52140] hover:bg-[#a81b36] text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Occupied Table Modal */}
      {selectedTable && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            {/* Modal Header */}
            <div className="border-b border-gray-200 p-6">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Table {selectedTable.name}</h3>
                  <p className="text-gray-600 text-sm mt-1">{selectedTable.description}</p>
                </div>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-gray-600 text-sm">Seating Capacity</p>
                  <p className="text-lg font-semibold text-gray-900">{selectedTable.seating_capacity}</p>
                </div>
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-gray-600 text-sm">Floor</p>
                  <p className="text-lg font-semibold text-gray-900">{selectedTable.floor}</p>
                </div>
              </div>

              <div>
                {/* <div className="flex justify-between items-center mb-4">
                  <h4 className="font-medium text-gray-900">Current Orders</h4>
                  <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-sm">
                    {tableOrders[selectedTable.id]?.length || 0} items
                  </span>
                </div> */}

                {/* {tableOrders[selectedTable.id]?.length > 0 ? (
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {tableOrders[selectedTable.id].map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <div>
                          <span className="font-medium text-gray-900">{item.name}</span>
                          <p className="text-gray-600 text-sm">₹{item.price}</p>
                        </div>
                        <span className="bg-white px-3 py-1 rounded-full text-sm font-medium border border-gray-200">
                          x{item.qty}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <span className="text-xl">📝</span>
                    </div>
                    <p className="text-gray-600">No active orders for this table</p>
                  </div>
                )} */}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 p-6">
              <div className="flex space-x-3">
                <button
                  onClick={closeModal}
                  className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    closeModal();
                    navigate(`${basePath}/singleorders/${selectedTable.order_id}`);
                  }}
                  className="flex-1 bg-[#c52140] text-white py-2.5 rounded-lg hover:bg-[#a81b36] transition-colors duration-200 font-medium"
                >
                  View Orders
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inactive Table Modal */}
      {inactiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            {/* Modal Header */}
            <div className="border-b border-gray-200 p-6">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Table {inactiveModal.name}</h3>
                  <p className="text-gray-600 text-sm mt-1">{inactiveModal.description}</p>
                </div>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="text-center py-6">
                <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <span className="text-2xl">⏸️</span>
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Table is Inactive</h4>
                <p className="text-gray-600 mb-4">
                  This table is currently inactive and cannot accept new orders. 
                  Please contact a manager to activate this table.
                </p>
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-gray-600 text-sm">Capacity</p>
                    <p className="text-lg font-semibold text-gray-900">{inactiveModal.seating_capacity}</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <p className="text-gray-600 text-sm">Floor</p>
                    <p className="text-lg font-semibold text-gray-900">{inactiveModal.floor}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 p-6">
              <div className="flex space-x-3">
                <button
                  onClick={closeModal}
                  className="flex-1 bg-gray-100 text-gray-700 py-2.5 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    closeModal();
                    // Add logic to activate table here
                    alert(`Activate table ${inactiveModal.name} logic would go here`);
                  }}
                  className="flex-1 bg-gray-800 text-white py-2.5 rounded-lg hover:bg-gray-900 transition-colors duration-200 font-medium"
                >
                  Activate Table
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TableSelectionScreen;